<template>
  <page closeable
        container="scrollable"
        modal
        uses-cards
        v-bind:is-loading="isLoading && !isRefreshing"
        v-bind:title="t('title')">
    <ion-refresher slot="fixed" v-on:ionRefresh="onRefresh">
      <ion-refresher-content/>
    </ion-refresher>

    <error-message-item v-bind:error-message="errorMessage"
                        v-on:retry="onRetry"/>

    <ion-list v-if="isLoading" class="list-with-cards">
      <site-manager-truck-list-item v-for="item in 3"
                                    v-bind:key="item"
                                    loading/>
    </ion-list>

    <ion-list v-else class="list-with-cards">
      <site-manager-truck-list-item v-for="truck of trucks"
                                    v-bind:key="truck.truck_registration"
                                    v-bind:truck="truck"
                                    v-on:select="onTruckSelected"/>
    </ion-list>
  </page>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {useI18n} from 'vue-i18n'
import {mapActions, mapGetters} from 'vuex'
import {getErrorMessage} from '@/exception/Exception'
import {RefresherCustomEvent} from '@/utils/RefresherCustomEvent'
import {IonList, IonRefresher, IonRefresherContent, modalController} from '@ionic/vue'
import {SiteManagerTruckBean} from '@/service/model/sitemanager/SiteManagerTruckBean'
import Page from '@/components/Page.vue'
import ErrorMessageItem from '@/components/common/ErrorMessageItem.vue'
import SiteManagerTruckListItem from '@/components/sitemanager/truck/SiteManagerTruckListItem.vue'

export default defineComponent({
  name: 'SiteManagerSelectTruckModal',
  data() {
    return {
      isLoading: true,
      isRefreshing: false,
      loadErrorMessage: undefined as string | undefined,
    }
  },
  setup() {
    const {t} = useI18n({
      inheritLocale: true,
      useScope: 'local'
    })
    return {t}
  },
  mounted() {
    this.loadTrucks(false)
  },
  methods: {
    ...mapActions('siteManager/truck', ['fetchTrucks', 'refreshTrucks']),
    async loadTrucks(refresh: boolean) {
      try {
        this.isLoading = true
        this.loadErrorMessage = undefined
        if (!refresh) {
          await this.fetchTrucks()
        } else {
          await this.refreshTrucks()
        }
      } catch (error) {
        this.loadErrorMessage = getErrorMessage(this.t, error)
      } finally {
        this.isLoading = false
      }
    },
    async onRetry() {
      await this.loadTrucks(false)
    },
    async onRefresh(event: RefresherCustomEvent) {
      this.isRefreshing = true
      try {
        await this.loadTrucks(true)
      } finally {
        event.detail.complete()
      }
      this.isRefreshing = false
    },
    onTruckSelected(truck: SiteManagerTruckBean) {
      modalController.dismiss(truck)
    }
  },
  computed: {
    ...mapGetters('siteManager/truck', ['trucksResult']),
    trucks(): Array<SiteManagerTruckBean> {
      return this.trucksResult?.item() || []
    },
    errorMessage(): string | undefined {
      return this.loadErrorMessage || this.trucksResult?.errorMessage(this.t('error.unknown'))
    },
  },
  components: {
    Page,
    ErrorMessageItem,
    IonRefresher, IonRefresherContent,
    IonList, SiteManagerTruckListItem
  }
})
</script>


<i18n locale="en">
{
  "title": "Select truck"
}
</i18n>

<i18n locale="fr">
{
  "title": "Choisir un camion"
}
</i18n>
